import {
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  Typography,
} from "@mui/material";
import type {
  MaterialCode,
  MaterialSupplierDetail,
} from "@/api/materialSupplierService";
import MaterialCodeCell from "./MaterialCodeCell";

interface ViewRowProps {
  row: MaterialCode;
  isSelected: boolean;
  onSelectChange: (row: MaterialCode, isSelected: boolean) => void;
  dimensionColumns: Array<{ parameter: string; name: string }>;
  formulaColumns: Array<{ parameter: string; name: string }>;
  otherColumns: Array<{ id: number; name: string }>;
  materialSupplierDetail?: MaterialSupplierDetail;
  isEditing?: boolean;
  isAdding?: boolean;
  materialData?: {
    loss_type?: string;
  };
  isOpenDetail?: boolean;
  isOpenDimension?: boolean;
  isOpenComponentPrice?: boolean;
  isOpenOtherCost?: boolean;
}

const ViewRow = ({
  row,
  isSelected,
  onSelectChange,
  dimensionColumns,
  formulaColumns,
  otherColumns,
  materialSupplierDetail,
  isEditing,
  isAdding,
  materialData,
  isOpenDetail = true,
  isOpenDimension = true,
  isOpenComponentPrice = true,
  isOpenOtherCost = true,
}: ViewRowProps) => {
  const roundingDigits = materialSupplierDetail?.attributes?.rounding_digits ?? 4;

  const findMatSpecs = (
    matTypeId: number,
  ): Array<{
    material_spec_id: number;
    spec_name: string;
    base_price: number;
  }> => {
    const specs = materialSupplierDetail?.material_type_ids?.find(
      (type) => type.id === matTypeId,
    )?.material_specs;
    return specs || [];
  };

  return (
    <TableRow sx={{ backgroundColor: "white" }}>
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: "white",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <Checkbox
          disabled={isEditing || isAdding}
          checked={isSelected}
          onChange={(e) => onSelectChange(row, e.target.checked)}
        />
      </TableCell>

      <MaterialCodeCell
        materialCode={row.material_code || ""}
        isAdding={false}
        width={500}
      />
      {isOpenDetail && (
        <>
          <TableCell sx={{ fontSize: "0.9rem" }}>
            {row.material_type_name}
          </TableCell>

          <TableCell sx={{ fontSize: "0.9rem" }}>
            {row.material_spec_name}
          </TableCell>

          <TableCell sx={{ fontSize: "0.9rem" }}>
            {row.second_tier_supplier_name}
          </TableCell>

          <TableCell sx={{ fontSize: "0.9rem" }}>
            {row.return_scrap_supplier_name}
          </TableCell>

          <TableCell sx={{ fontSize: "0.9rem" }}>
            {row.first_tier_supplier_name}
          </TableCell>
          <TableCell
            sx={{
              fontSize: "0.9rem",
            }}
          >
            <Tooltip title={row.ref_part_no || ""} placement="top">
              <Typography 
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '350px',
                }}
              >
                {row.ref_part_no || ""}
              </Typography>
            </Tooltip>
          </TableCell>
        </>
      )}
      {isOpenDimension &&
        dimensionColumns.map((col) => (
          <TableCell
            key={col.parameter}
            sx={{ textAlign: "right", fontSize: "1rem" }}
          >
            {String((row as Record<string, unknown>)[col.parameter] || 0)}
          </TableCell>
        ))}

      <TableCell sx={{ textAlign: "right", fontSize: "1rem" }}>
        {(row.material_price ?? 0).toFixed(roundingDigits)}
      </TableCell>

      <TableCell sx={{ textAlign: "right", fontSize: "1rem" }}>
        {(
          findMatSpecs(row.material_type_id)?.find((spec) => {
            return spec.material_spec_id === row.material_spec_id;
          })?.base_price ?? 0
        ).toFixed(4)}
      </TableCell>

      {isOpenComponentPrice &&
        formulaColumns.map((col) => {
          const isLossParam =
            col.parameter === "loss1" || col.parameter === "loss2";

          if (isLossParam) {
            const value = col.parameter === "loss1" ? row.loss1 : row.loss2;
            const lossType = materialData?.loss_type?.trim().toLowerCase();
            const isPct =
              lossType === "mix"
                ? col.parameter === "loss1"
                  ? row.is_loss1_pct
                  : row.is_loss2_pct
                : lossType === "pct"
                  ? true
                  : false;

            return (
              <TableCell
                colSpan={2}
                key={col.parameter}
                sx={{ textAlign: "right", fontSize: "1rem" }}
              >
                {(value || 0).toFixed(2)} {isPct ? "%" : "THB/pcs"}
              </TableCell>
            );
          }

          return (
            <TableCell
              key={col.parameter}
              sx={{ textAlign: "right", fontSize: "1rem" }}
            >
              {(Number((row as Record<string, unknown>)[col.parameter] || 0)).toFixed(2)}
            </TableCell>
          );
        })}

      {isOpenOtherCost &&
        otherColumns.map((col) => (
          <TableCell key={col.id} sx={{ textAlign: "right", fontSize: "1rem" }}>
            {(
              (row as MaterialCode).others_cost_detail.find(
                (detail) => detail.id === col.id,
              )?.cost || 0
            ).toFixed(2)}
          </TableCell>
        ))}
    </TableRow>
  );
};

export default ViewRow;
